import { useCallback, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { useToasts } from 'react-toast-notifications';
import Button from '../../components/Button';
import Error from '../../components/Error';
import { FormSection, FormSectionBody, FormSectionHeader, StyledForm } from '../../components/Form';
import Header from '../../components/Header';
import IconInvoice from '../../components/icons/IconInvoice';
import { StripeMode } from '../../types/pricingPage';
import { useAxios } from '../../utils/hooks';
import VALIDATORS from '../../utils/validators';
import c from 'classnames';

const MODES: FormData['mode'][] = [StripeMode.LIVE, StripeMode.TEST];

interface FormData {
    mode: StripeMode;
    invoiceId: string;
    companyName: string;
    companyAddress: string;
    companyCity: string;
    companyZip: string;
    companyCountry: string;
    ourCompanyName?: string;
    recieptNumber?: string;
}

export default function InvoiceGenerator() {
    const axios = useAxios();
    const { addToast } = useToasts();
    const [pdfData, setPdfData] = useState<Blob>();

    const handleGenerateInvoice = useCallback(
        (data: FormData) => {
            if (!axios || !axios.current) {
                return Promise.resolve();
            }
            return axios.current
                ?.post<Blob>('/invoices/generate', data, { responseType: 'blob' })
                .then((res) => {
                    setPdfData(res.data);
                })
                .catch((err) => {
                    console.error(err);
                    addToast('Something went wrong. Please try again.', { appearance: 'error' });
                });
        },
        [addToast, axios]
    );

    return (
        <div className="w-full">
            <Header>
                <IconInvoice className="inline w-6 mr-3" /> Generate Stripe Invoice PDF
            </Header>
            <Form<FormData> initialValues={{ mode: StripeMode.LIVE }} onSubmit={handleGenerateInvoice}>
                {({ handleSubmit, submitting, invalid, values }) => (
                    <StyledForm onSubmit={handleSubmit} className="w-full mt-4 shadow">
                        <FormSection>
                            <FormSectionHeader>Stripe Invoice</FormSectionHeader>
                            <FormSectionBody>
                                <div className="mb-2">
                                    <label className="text-sm text-gray-600">Stripe Mode</label>
                                    <div className="flex justify-start space-x-2">
                                        {MODES.map((mode) => (
                                            <div className="form-check form-check-inline">
                                                <Field name="mode" type="radio" value={mode}>
                                                    {(props) => (
                                                        <label>
                                                            <input {...props.input} className="form-radio mr-2" />
                                                            <span
                                                                className={c(
                                                                    'form-check-label inline-block text-gray-800',
                                                                    {
                                                                        'text-green-500': mode === StripeMode.LIVE,
                                                                    }
                                                                )}
                                                            >
                                                                {mode}
                                                            </span>
                                                        </label>
                                                    )}
                                                </Field>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="mb-2">
                                    <label className="text-sm text-gray-600">Stripe Invoice ID</label>
                                    <Field
                                        name="invoiceId"
                                        component="input"
                                        type="text"
                                        validate={VALIDATORS.required}
                                    >
                                        {({ input, meta }) => (
                                            <>
                                                <input
                                                    {...input}
                                                    placeholder="in_1MU1cXDMcRSELXUGw8Rll8ir"
                                                    className="input w-full"
                                                />
                                                {meta.error && (
                                                    <span className="ml-2">
                                                        <Error message={meta.error} />
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    </Field>
                                </div>
                            </FormSectionBody>
                        </FormSection>
                        <FormSection>
                            <FormSectionHeader>Invoice Data</FormSectionHeader>
                            <FormSectionBody>
                                <div className="mb-2">
                                    <label className="text-sm text-gray-600">From Company (optional)</label>
                                    <Field name="ourCompanyName" component="input" type="text">
                                        {({ input, meta }) => (
                                            <>
                                                <input {...input} placeholder="Your Co Ltd" className="input w-full" />
                                                {meta.error && (
                                                    <span className="ml-2">
                                                        <Error message={meta.error} />
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    </Field>
                                </div>
                                <div className="mb-2">
                                    <label className="text-sm text-gray-600">Company PO Number (optional)</label>
                                    <Field name="recieptNumber" component="input" type="text">
                                        {({ input, meta }) => (
                                            <>
                                                <input {...input} placeholder="PO-1234" className="input w-full" />
                                                {meta.error && (
                                                    <span className="ml-2">
                                                        <Error message={meta.error} />
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    </Field>
                                </div>
                            </FormSectionBody>
                        </FormSection>
                        <FormSection>
                            <FormSectionHeader>Customer Address</FormSectionHeader>
                            <FormSectionBody>
                                <div className="mb-2">
                                    <label className="text-sm text-gray-600">Company Name</label>
                                    <Field
                                        name="companyName"
                                        component="input"
                                        type="text"
                                        validate={VALIDATORS.required}
                                    >
                                        {({ input, meta }) => (
                                            <>
                                                <input {...input} placeholder="Acme Co Ltd" className="input w-full" />
                                                {meta.error && (
                                                    <span className="ml-2">
                                                        <Error message={meta.error} />
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    </Field>
                                </div>
                                <div className="mb-2">
                                    <label className="text-sm text-gray-600">Company Address Line 1</label>
                                    <Field
                                        name="companyAddress"
                                        component="input"
                                        type="text"
                                        validate={VALIDATORS.required}
                                    >
                                        {({ input, meta }) => (
                                            <>
                                                <input {...input} placeholder="1 The Street" className="input w-full" />
                                                {meta.error && (
                                                    <span className="ml-2">
                                                        <Error message={meta.error} />
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    </Field>
                                </div>
                                <div className="mb-2">
                                    <label className="text-sm text-gray-600">Company Address City</label>
                                    <Field
                                        name="companyCity"
                                        component="input"
                                        type="text"
                                        validate={VALIDATORS.required}
                                    >
                                        {({ input, meta }) => (
                                            <>
                                                <input
                                                    {...input}
                                                    placeholder="San Francisco"
                                                    className="input w-full"
                                                />
                                                {meta.error && (
                                                    <span className="ml-2">
                                                        <Error message={meta.error} />
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    </Field>
                                </div>
                                <div className="mb-2">
                                    <label className="text-sm text-gray-600">Company Address Zip/Postal Code</label>
                                    <Field
                                        name="companyZip"
                                        component="input"
                                        type="text"
                                        validate={VALIDATORS.required}
                                    >
                                        {({ input, meta }) => (
                                            <>
                                                <input {...input} placeholder="90210" className="input block" />
                                                {meta.error && (
                                                    <span className="ml-2">
                                                        <Error message={meta.error} />
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    </Field>
                                </div>
                                <div className="mb-2">
                                    <label className="text-sm text-gray-600">Company Address Country</label>
                                    <Field
                                        name="companyCountry"
                                        component="input"
                                        type="text"
                                        validate={VALIDATORS.required}
                                    >
                                        {({ input, meta }) => (
                                            <>
                                                <input {...input} placeholder="USA" className="input block" />
                                                {meta.error && (
                                                    <span className="ml-2">
                                                        <Error message={meta.error} />
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    </Field>
                                </div>
                                <div className="flex">
                                    {pdfData && (
                                        <a
                                            href={URL.createObjectURL(pdfData)}
                                            download={`invoice-${values.invoiceId}.pdf`}
                                        >
                                            Download Invoice
                                        </a>
                                    )}
                                    <Button
                                        type="submit"
                                        kind="tertiary"
                                        className="ml-auto"
                                        state={submitting ? 'SAVING' : 'READY'}
                                        disabled={invalid}
                                    >
                                        Generate Invoice
                                    </Button>
                                </div>
                            </FormSectionBody>
                        </FormSection>
                    </StyledForm>
                )}
            </Form>
        </div>
    );
}
